import {useEffect, useMemo, useRef} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import {codeFileFromRoute, codeSelectionUrl, searchWithoutFilesLink, serviceFileFromFilesLink} from '../utils/codeRoute';

/**
 * Canvas's selection → URL sync: tells `onSelectionChange` (App's syncUrlWithCanvasSelections) when the service, the
 * branch or the tab change, with the file already in the URL (the route's trailing `*`) so it is kept. File changes
 * are RepositoryTree's to put in the URL; the line (`#L42`) is kept by the sync itself (utils/codeRoute.js).
 * Held back while a service in the URL is still waiting to be resolved (`urlInitPendingRef`, or no selection yet), so
 * a still-null first selection never rewrites a deep link down to the bare project.
 */
export function useSelectionUrlSync({selectedResource, selectedRevision, activeTab, onSelectionChange, urlInitPendingRef, hold = false}) {
  const params = useParams();
  const previousRef = useRef({});
  const selectedMicroservice = selectedResource?.type === 'service' || selectedResource?.type === 'configuration' ? selectedResource.data : null;
  useEffect(() => {
    if (!onSelectionChange) { return; }
    if (urlInitPendingRef?.current) { return; }
    // A Files link is being turned into its file's Code-tab URL (useFilesLinkToService). The app's router commits a
    // navigate's location in a transition (v7_startTransition), after the selection set beside it: syncing in between
    // wrote the service's Code URL over the file's, with the stale `?view=files&path=…`, and no file opened.
    if (hold) { return; }
    // Nothing selected yet while the URL names a service: the selection is on its way (the init effect that resolves
    // it sets its state in the same commit this runs in). Syncing now rewrote a deep link down to the bare project,
    // dropping its file and line.
    if (!selectedResource && params.microservice) { return; }
    // The Home pseudo-resource ('Project Configuration') is never a service segment in the URL.
    const microserviceName = selectedResource?.type === 'configuration' ? null : selectedMicroservice?.name || null;
    const branchName = selectedRevision?.name || null;
    const tab = activeTab;
    const previous = previousRef.current;
    if (previous.microserviceName === microserviceName && previous.branchName === branchName && previous.tab === tab) { return; }
    previousRef.current = {microserviceName, branchName, tab};
    // The file in the URL stays: the route's own trailing `*` (App.jsx's <Route path="/code/:tenantId?/.../*">).
    const currentFilePath = params['*'];
    let decodedFilePath = currentFilePath || null;
    try { decodedFilePath = currentFilePath ? decodeURIComponent(currentFilePath) : null; } catch { /* keep it as is */ }
    onSelectionChange(microserviceName, branchName, tab, decodedFilePath, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMicroservice, selectedRevision, activeTab, onSelectionChange, hold]);
}

/**
 * A Files-view link to a file inside a service (`?view=files&path=services/app/server.js&line=12`: the chat's file
 * names, "Open the code" from Traces and Quality) opens that service's Code tab on the file and line: `onOpenService`
 * selects the service (Canvas's state), and the URL becomes the Code-tab URL of the file
 * (`/code/<tenant>/<project>/services%2Fapp/<branch>/code/server.js#L12`, the rest of the query string kept), which
 * the Code tab opens like any deep link. Waits for the project's services and branch. A path in no service (the
 * project root, `brain/`) is left to Project Home's Files view.
 * Returns the link being opened, or null, so the Home view's own handling of `?view=files` stays out of the way.
 */
export function useFilesLinkToService({configuration, selectedRevision, tenantId, projectId, onOpenService}) {
  const location = useLocation();
  const navigate = useNavigate();
  const serviceNames = useMemo(() => Object.keys(configuration?.services || {}), [configuration]);
  const target = useMemo(() => serviceFileFromFilesLink(location.search, serviceNames), [location.search, serviceNames]);
  const onOpenRef = useRef(onOpenService);
  onOpenRef.current = onOpenService;
  const branch = selectedRevision?.name || null;
  useEffect(() => {
    if (!target || !branch || !tenantId || !projectId) { return; }
    const service = configuration?.services?.[target.service];
    if (!service) { return; }
    onOpenRef.current?.(target.service, service);
    navigate(codeSelectionUrl({
      tenantId, projectId, service: target.service, branch, tab: 'code', filePath: target.path, line: target.line,
      search: searchWithoutFilesLink(location.search),
    }), {replace: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, branch, tenantId, projectId]);
  return target;
}

/**
 * The file a Code-tab URL names, with its line (`.../code/server.js#L30`, utils/codeRoute.js), the line kept while the
 * URL stays on that file: the URL can lose its `#L30` before the editor has the file open (a replaceState from a
 * selection sync or a view's own URL bookkeeping, while the project is still loading), and the file then opened on line 1.
 */
export function useRouteCodeFile() {
  const params = useParams();
  const location = useLocation();
  const file = codeFileFromRoute(params, location);
  const key = file.path ? `${params.microservice || ''}/${params.branch || ''}:${file.path}` : null;
  const latchedRef = useRef(null);
  if (key && file.line) {
    latchedRef.current = {key, line: file.line};
  } else if (latchedRef.current && latchedRef.current.key !== key) {
    latchedRef.current = null;
  }
  return file.path && !file.line && latchedRef.current ? {path: file.path, line: latchedRef.current.line} : file;
}
